/**
 * OWL2 RDF/XML Parser
 *
 * Parses RDF/XML format into OWL2 Ontology by:
 * 1. Extracting xmlns: prefix declarations from the XML
 * 2. Using rdfxml-streaming-parser to parse RDF/XML to RDF/JS quads
 * 3. Loading quads into N3.Store
 * 4. Reusing Owl2TurtleParser's parseFromStore() for OWL2 axiom reconstruction
 */

import { RdfXmlParser } from 'rdfxml-streaming-parser';
import { Store } from 'n3';
import type { Quad } from 'n3';
import type { Ontology } from '../types/Ontology.ts';
import { Owl2TurtleParser, type ParseOptions } from '../turtle/parser.ts';

/**
 * Parses RDF/XML format into an OWL2 Ontology.
 *
 * This is a thin adapter that converts RDF/XML to quads using rdfxml-streaming-parser,
 * stores them in N3.Store, and delegates to Owl2TurtleParser for OWL2 axiom reconstruction.
 */
export class Owl2RdfXmlParser {
    /**
     * Parse RDF/XML string to Ontology (async).
     *
     * @param rdfxml - The RDF/XML content to parse
     * @param options - Optional parsing options including workspace for resolving external references
     */
    async parse(rdfxml: string, options?: ParseOptions): Promise<Ontology> {
        const { store, prefixes } = await this.parseToStore(rdfxml);
        const turtleParser = new Owl2TurtleParser();
        return turtleParser.parseFromStore(store, prefixes, options);
    }

    /**
     * Extract xmlns prefix declarations from RDF/XML content.
     *
     * The rdfxml-streaming-parser does not emit 'prefix' events, so we extract
     * xmlns:prefix="namespace" declarations directly from the XML.
     *
     * @param rdfxml - The RDF/XML content
     * @returns Map of prefix name to namespace IRI
     */
    private extractPrefixes(rdfxml: string): Map<string, string> {
        const prefixes = new Map<string, string>();

        // Match xmlns:prefix="namespace" declarations
        // This regex handles both single and double quotes, and multiline content
        const xmlnsRegex = /xmlns:([a-zA-Z][a-zA-Z0-9_-]*)=["']([^"']+)["']/g;
        let match;

        while ((match = xmlnsRegex.exec(rdfxml)) !== null) {
            const prefix = match[1];
            const namespace = match[2];
            // Only add if not already present (first occurrence wins)
            if (!prefixes.has(prefix)) {
                prefixes.set(prefix, namespace);
            }
        }

        // Also extract default namespace xmlns="..." as empty prefix
        const defaultNsRegex = /xmlns=["']([^"']+)["']/;
        const defaultMatch = defaultNsRegex.exec(rdfxml);
        if (defaultMatch && !prefixes.has('')) {
            prefixes.set('', defaultMatch[1]);
        }

        return prefixes;
    }

    /**
     * Parse RDF/XML to N3.Store and extract prefixes.
     *
     * @param rdfxml - The RDF/XML content to parse
     * @returns Object containing the N3.Store and prefixes map
     */
    private parseToStore(rdfxml: string): Promise<{ store: Store; prefixes: Map<string, string> }> {
        return new Promise((resolve, reject) => {
            const store = new Store();

            // Extract prefixes from xmlns declarations in the XML
            const prefixes = this.extractPrefixes(rdfxml);

            const parser = new RdfXmlParser();

            const quads: Quad[] = [];

            parser.on('data', (quad: Quad) => {
                quads.push(quad);
            });

            parser.on('error', (error: Error) => {
                reject(error);
            });

            parser.on('end', () => {
                // Add all quads to store
                for (const quad of quads) {
                    store.addQuad(quad);
                }
                resolve({ store, prefixes });
            });

            // Write the RDF/XML content and signal end
            parser.write(rdfxml);
            parser.end();
        });
    }
}

/**
 * Convenience function to parse RDF/XML string to Ontology.
 *
 * @param rdfxml - The RDF/XML content to parse
 * @param options - Optional parsing options including workspace for resolving external references
 */
export async function parseRdfXml(rdfxml: string, options?: ParseOptions): Promise<Ontology> {
    const parser = new Owl2RdfXmlParser();
    return parser.parse(rdfxml, options);
}
